// components/sales/SalesCallDetail.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Flag,
  Clock,
  Calendar,
  User,
  Edit3,
  Save,
  X,
  Play,
  Pause,
  MoreVertical,
  RefreshCw,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { ExportCurve, DocumentDownload } from "iconsax-reactjs";
import api from "../../services/api";

// ─── Helpers ────────────────────────────────────────────────────────────────

const OUTCOME_CONFIG = {
  SOLD: { label: "Sold", color: "text-emerald-400 bg-emerald-400/10" },
  QUAL: { label: "Qualified", color: "text-green-400 bg-green-400/10" },
  SAT_QUAL: { label: "Sat. Qualified", color: "text-cyan-400 bg-cyan-400/10" },
  NON_QUAL: {
    label: "Non-Qualified",
    color: "text-orange-400 bg-orange-400/10",
  },
  NON_SAT: { label: "Non-Sat.", color: "text-yellow-400 bg-yellow-400/10" },
  NOT_INTERESTED: {
    label: "Not Interested",
    color: "text-red-400 bg-red-400/10",
  },
};

const formatDuration = (s) => {
  if (!s) return "0s";
  const m = Math.floor(s / 60),
    sec = Math.floor(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const formatTime = (s) => {
  const sec = Math.max(0, Math.floor(s));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
};
const formatTranscriptTime = (str) => {
  const s = parseFloat(str);
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const getScoreColor = (n) =>
  n >= 75
    ? "text-green-400"
    : n >= 50
      ? "text-yellow-400"
      : n >= 25
        ? "text-orange-400"
        : "text-red-400";

const getSentimentColor = (s) => {
  switch (s?.toLowerCase()) {
    case "positive":
      return "text-green-400";
    case "negative":
      return "text-red-400";
    default:
      return "text-yellow-400";
  }
};

// ─── Qualification Section ───────────────────────────────────────────────────

const SalesQualificationDetails = ({ qualification, onOutcomeUpdate }) => {
  const [editingOutcome, setEditingOutcome] = useState(false);
  const [form, setForm] = useState({ manualOutcome: "", manualNotes: "" });

  if (!qualification) {
    return (
      <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-2">Sales Qualification</h2>
        <p className="text-sm text-gray-400">
          No qualification data available for this call yet.
        </p>
      </div>
    );
  }

  const effectiveOutcome =
    qualification.manualOutcome || qualification.suggestedOutcome;
  const outcomeCfg = OUTCOME_CONFIG[effectiveOutcome] || {
    label: effectiveOutcome || "—",
    color: "text-gray-400 bg-gray-400/10",
  };

  const handleSaveOutcome = async () => {
    await onOutcomeUpdate(form);
    setEditingOutcome(false);
  };

  return (
    <div className="space-y-4">
      {/* Scores */}
      <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Sales Qualification</h2>
          <button
            onClick={() => {
              setForm({
                manualOutcome:
                  qualification.manualOutcome ||
                  qualification.suggestedOutcome ||
                  "",
                manualNotes: qualification.manualNotes || "",
              });
              setEditingOutcome(true);
            }}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Edit3 size={13} /> Override Outcome
          </button>
        </div>

        {/* Score cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            {
              label: "Qual. Score",
              value: qualification.qualificationScore,
              sub: qualification.qualificationLevel?.toUpperCase(),
            },
            { label: "Presentation", value: qualification.presentationScore },
            {
              label: "Objection Handling",
              value: qualification.objectionHandlingScore,
              sub: `${qualification.objectionsEncountered?.length || 0} objections`,
            },
          ].map(({ label, value, sub }) => (
            <div
              key={label}
              className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4"
            >
              <div className="text-xs text-gray-400 mb-1">{label}</div>
              <div
                className={`text-3xl font-bold ${getScoreColor(value || 0)}`}
              >
                {value ?? "—"}
              </div>
              {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
            </div>
          ))}
        </div>

        {/* Outcome */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${outcomeCfg.color}`}
          >
            {outcomeCfg.label}
          </div>
          {qualification.manualOutcome &&
            qualification.suggestedOutcome &&
            qualification.manualOutcome !== qualification.suggestedOutcome && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                AI suggested:{" "}
                {OUTCOME_CONFIG[qualification.suggestedOutcome]?.label ||
                  qualification.suggestedOutcome}
              </div>
            )}
          {qualification.isQualified ? (
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-400/10 text-green-400">
              <CheckCircle size={12} /> Qualified
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-400/10 text-red-400">
              <XCircle size={12} /> Not Qualified
            </div>
          )}
        </div>

        {/* Reasoning */}
        {qualification.qualificationReasoning && (
          <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
            <div className="text-xs text-gray-400 mb-2">AI Reasoning</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              {qualification.qualificationReasoning}
            </p>
          </div>
        )}

        {/* Manual notes */}
        {qualification.manualNotes && (
          <div className="mt-3 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
            <div className="text-xs text-purple-400 mb-1">
              Manual Review Notes
            </div>
            <p className="text-sm text-gray-300">{qualification.manualNotes}</p>
          </div>
        )}
      </div>

      {/* Override modal */}
      {editingOutcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Override Outcome</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">
                  Outcome
                </label>
                <select
                  value={form.manualOutcome}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, manualOutcome: e.target.value }))
                  }
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="">Select outcome...</option>
                  {Object.entries(OUTCOME_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">
                  Notes
                </label>
                <textarea
                  value={form.manualNotes}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, manualNotes: e.target.value }))
                  }
                  rows={3}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                  placeholder="Add review notes..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditingOutcome(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOutcome}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Details */}
      <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Meeting Details</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Meeting Quality", value: qualification.meetingQuality },
            {
              label: "Customer Engagement",
              value: qualification.customerEngagement,
            },
            {
              label: "Decision Maker",
              value:
                qualification.decisionMakerPresent === true
                  ? `Yes — ${qualification.decisionMakerName || "Present"}`
                  : qualification.decisionMakerPresent === false
                    ? "No"
                    : "—",
            },
            { label: "Timeline", value: qualification.timeline || "—" },
            { label: "Urgency", value: qualification.urgencyLevel || "—" },
            {
              label: "Solution Presented",
              value: qualification.solutionPresented
                ? `Yes (${qualification.solutionReception || "no feedback"})`
                : "No",
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-3"
            >
              <div className="text-xs text-gray-400 mb-1">{label}</div>
              <div className="text-sm text-white capitalize">
                {value || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Criteria */}
      <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Business Criteria</h3>
        <div className="grid grid-cols-2 gap-4">
          {/* Spend */}
          <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="text-sm font-medium text-gray-300">
                Monthly Spend
              </div>
              {qualification.meetsSpendThreshold ? (
                <div className="text-green-400 text-sm">✓</div>
              ) : (
                <div className="text-red-400 text-sm">✗</div>
              )}
            </div>
            {qualification.currentSpendDiscussed ? (
              <div className="text-2xl font-bold text-white">
                {qualification.currentSpend != null
                  ? `£${qualification.currentSpend}`
                  : "Not specified"}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Not discussed</div>
            )}
          </div>

          {/* System Age */}
          <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="text-sm font-medium text-gray-300">
                System Age
              </div>
            </div>
            {qualification.systemAgeDiscussed ? (
              <div className="text-2xl font-bold text-white">
                {qualification.systemAge} yrs
              </div>
            ) : (
              <div className="text-sm text-gray-500">Not discussed</div>
            )}
            {qualification.currentSystem && (
              <div className="text-xs text-gray-400 mt-1">
                {qualification.currentSystem}
              </div>
            )}
          </div>

          {/* Handsets */}
          <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
            <div className="text-sm font-medium text-gray-300 mb-2">
              Handsets
            </div>
            {qualification.handsetsDiscussed ? (
              <div className="text-2xl font-bold text-white">
                {qualification.numberOfHandsets}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Not discussed</div>
            )}
          </div>

          {/* Contract */}
          <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
            <div className="text-sm font-medium text-gray-300 mb-2">
              Contract Remaining
            </div>
            {qualification.contractDiscussed ? (
              <div className="text-2xl font-bold text-white">
                {qualification.contractRemaining} yrs
              </div>
            ) : (
              <div className="text-sm text-gray-500">Not discussed</div>
            )}
          </div>
        </div>
      </div>

      {/* Next Steps & Contact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Next Steps */}
        {qualification.nextSteps?.length > 0 && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-5">
            <h3 className="font-semibold mb-3">Next Steps</h3>
            <ul className="space-y-2">
              {qualification.nextSteps.map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-300"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />
                  {typeof step === "string"
                    ? step
                    : step.action || JSON.stringify(step)}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-3">
              {qualification.proposalRequested && (
                <span className="text-xs px-2 py-0.5 bg-blue-400/10 text-blue-400 rounded-full">
                  Proposal Requested
                </span>
              )}
              {qualification.secondMeetingScheduled && (
                <span className="text-xs px-2 py-0.5 bg-purple-400/10 text-purple-400 rounded-full">
                  Second Meeting Scheduled
                </span>
              )}
            </div>
          </div>
        )}

        {/* Contact */}
        {(qualification.fullName ||
          qualification.email ||
          qualification.jobTitle) && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-5">
            <h3 className="font-semibold mb-3">Contact Confirmed</h3>
            <div className="space-y-2 text-sm">
              {qualification.fullName && (
                <div>
                  <span className="text-gray-400">Name: </span>
                  <span className="text-white">{qualification.fullName}</span>
                </div>
              )}
              {qualification.jobTitle && (
                <div>
                  <span className="text-gray-400">Title: </span>
                  <span className="text-white">{qualification.jobTitle}</span>
                </div>
              )}
              {qualification.email && (
                <div>
                  <span className="text-gray-400">Email: </span>
                  <span className="text-white">{qualification.email}</span>
                </div>
              )}
              {qualification.fullAddress && (
                <div>
                  <span className="text-gray-400">Address: </span>
                  <span className="text-white">
                    {qualification.fullAddress}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pain Points & Objections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {qualification.painPoints?.length > 0 && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-5">
            <h3 className="font-semibold mb-3">Pain Points</h3>
            <ul className="space-y-2">
              {qualification.painPoints.map((pt, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-300"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 bg-orange-400 rounded-full flex-shrink-0" />
                  {typeof pt === "string" ? pt : pt.point || JSON.stringify(pt)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {qualification.objectionsEncountered?.length > 0 && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-5">
            <h3 className="font-semibold mb-3">Objections</h3>
            <ul className="space-y-2">
              {qualification.objectionsEncountered.map((obj, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-300"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  {typeof obj === "string"
                    ? obj
                    : obj.objection || JSON.stringify(obj)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const SalesCallDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState("");

  // Audio
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const transcriptRefs = useRef({});

  useEffect(() => {
    fetchCallDetails();
  }, [id]);

  const fetchCallDetails = async () => {
    try {
      setLoading(true);
      const response = await api.getSalesCallDetails(id);
      if (response.success) {
        setCall(response.call);
        setNotes(response.call.metadata?.notes || "");
        setAudioDuration(response.call.duration || 0);
      } else {
        setError("Failed to fetch call details");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // ── Audio controls ──────────────────────────────────────────────────────────
  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration || 0);
      setCurrentTime(0);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(Math.floor(audioRef.current.currentTime));
      scrollToCurrentTranscript(audioRef.current.currentTime);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !audioDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * audioDuration;
    setCurrentTime(Math.floor(pct * audioDuration));
  };

  const scrollToCurrentTranscript = (t) => {
    if (!call?.transcripts?.length || !transcriptContainerRef.current) return;
    const idx = call.transcripts.findIndex((tr, i) => {
      const next = call.transcripts[i + 1];
      return (
        t >= parseFloat(tr.startTime) &&
        t < (next ? parseFloat(next.startTime) : audioDuration)
      );
    });
    if (idx === -1) return;
    const el = transcriptRefs.current[idx];
    const container = transcriptContainerRef.current;
    if (el && container) {
      container.scrollTo({
        top: el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2,
        behavior: "smooth",
      });
    }
  };

  const handleTranscriptTimeClick = (startTime) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = parseFloat(startTime);
    setCurrentTime(Math.floor(parseFloat(startTime)));
    if (!isPlaying) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const isTranscriptActive = (startTime, index) => {
    const start = parseFloat(startTime);
    const next = call?.transcripts[index + 1];
    return (
      currentTime >= start &&
      currentTime < (next ? parseFloat(next.startTime) : audioDuration)
    );
  };

  // ── Outcome update ──────────────────────────────────────────────────────────
  const handleOutcomeUpdate = async (formData) => {
    try {
      await api.updateSalesCallOutcome(id, formData);
      await fetchCallDetails();
    } catch (err) {
      alert("Failed to update outcome: " + err.message);
    }
  };

  // ── Notes ───────────────────────────────────────────────────────────────────
  const handleSaveNotes = async () => {
    try {
      await api.updateSalesCallMetadata(id, { notes });
      setEditingNotes(false);
      fetchCallDetails();
    } catch (err) {
      alert("Failed to save notes");
    }
  };

  const handleExportTranscript = () => {
    if (!call?.transcripts?.length) {
      alert("No transcript available");
      return;
    }
    const content = call.transcripts
      .map(
        (t) =>
          `[${formatTranscriptTime(t.startTime)}] ${t.speakerLabel || "Speaker"}: ${t.text}`,
      )
      .join("\n\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-transcript-${call.id}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (error || !call) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || "Call not found"}</p>
          <button
            onClick={() => navigate("/sales-transcripts")}
            className="text-purple-400 hover:text-purple-300"
          >
            ← Back to Sales Calls
          </button>
        </div>
      </div>
    );
  }

  const effectiveOutcome =
    call.qualification?.manualOutcome || call.qualification?.suggestedOutcome;
  const outcomeCfg = OUTCOME_CONFIG[effectiveOutcome];
  const progressPct =
    audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white p-6">
      {/* Back */}
      <button
        onClick={() => navigate("/sales-transcripts")}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft size={18} /> Back to Sales Calls
      </button>

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">
            {call.externalUserName || call.externalUserId}
          </h1>
          <div className="flex flex-wrap gap-3 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Calendar size={14} /> {formatDate(call.callStartTime)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} /> {formatDuration(call.duration)}
            </span>
            <span className="flex items-center gap-1">
              <User size={14} /> {call.externalAppointmentId}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {outcomeCfg && (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${outcomeCfg.color}`}
            >
              {outcomeCfg.label}
            </span>
          )}
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
            ${
              call.status === "completed"
                ? "bg-green-400/10 text-green-400"
                : call.status === "failed"
                  ? "bg-red-400/10 text-red-400"
                  : "bg-orange-400/10 text-orange-400"
            }`}
          >
            {call.status}
          </span>
          <button
            onClick={handleExportTranscript}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#13161B] border border-[#22262F] rounded-lg text-sm hover:bg-[#1A1D24] transition-colors"
          >
            <ExportCurve size={15} /> Export
          </button>
        </div>
      </div>

      {/* Audio Player */}
      {call.recordingUrl && (
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-4 mb-6">
          <audio
            ref={audioRef}
            src={call.recordingUrl}
            onLoadedMetadata={handleAudioLoadedMetadata}
            onTimeUpdate={handleAudioTimeUpdate}
            onEnded={() => setIsPlaying(false)}
            muted={isMuted}
          />
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (!audioRef.current) return;
                if (isPlaying) {
                  audioRef.current.pause();
                  setIsPlaying(false);
                } else {
                  audioRef.current.play();
                  setIsPlaying(true);
                }
              }}
              className="w-10 h-10 flex items-center justify-center bg-purple-600 rounded-full hover:bg-purple-700 transition-colors flex-shrink-0"
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <div className="flex-1">
              <div
                className="h-2 bg-[#22262F] rounded-full cursor-pointer relative"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-purple-500 rounded-full transition-all duration-100"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(audioDuration)}</span>
              </div>
            </div>
            <button
              onClick={() => {
                setIsMuted((m) => !m);
                if (audioRef.current) audioRef.current.muted = !isMuted;
              }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              {isMuted ? "🔇" : "🔊"}
            </button>
          </div>
        </div>
      )}

      {/* Body: left col + transcript */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left column */}
        <div className="lg:w-1/2 space-y-6">
          {/* Summary */}
          {call.insights?.summary && (
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-3">Summary</h2>
              <p className="text-sm text-gray-300 leading-relaxed">
                {call.insights.summary}
              </p>
              {call.insights.averageSentiment && (
                <div
                  className={`mt-3 text-sm font-medium ${getSentimentColor(call.insights.averageSentiment)}`}
                >
                  {call.insights.averageSentiment.charAt(0).toUpperCase() +
                    call.insights.averageSentiment.slice(1)}{" "}
                  sentiment
                </div>
              )}
            </div>
          )}

          {/* Action Items */}
          {call.insights?.actionItems?.length > 0 && (
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Action Items</h2>
              <ul className="space-y-3">
                {call.insights.actionItems.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-gray-300"
                  >
                    <span className="mt-1 w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />
                    <div>
                      <span>{item.item || item}</span>
                      {item.speaker && (
                        <span className="text-xs text-gray-400 block mt-0.5">
                          — {item.speaker}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Notes */}
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Notes</h2>
              {editingNotes ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveNotes}
                    className="p-1 text-green-400 hover:text-green-300"
                  >
                    <Save size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditingNotes(false);
                      setNotes(call.metadata?.notes || "");
                    }}
                    className="p-1 text-red-400 hover:text-red-300"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="p-1 text-gray-400 hover:text-white"
                >
                  <Edit3 size={16} />
                </button>
              )}
            </div>
            {editingNotes ? (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                placeholder="Add notes about this visit..."
              />
            ) : (
              <p className="text-sm text-gray-300 min-h-[60px]">
                {notes || "No notes added yet."}
              </p>
            )}
          </div>

          {/* Qualification */}
          <SalesQualificationDetails
            qualification={call.qualification}
            onOutcomeUpdate={handleOutcomeUpdate}
          />
        </div>

        {/* Transcript */}
        <div className="lg:w-1/2">
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl h-[800px] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-[#22262F] flex-shrink-0">
              <h2 className="text-lg font-semibold">Transcript</h2>
              <div className="flex items-center gap-2">
                {call.insights?.averageSentiment && (
                  <span
                    className={`text-xs font-medium ${getSentimentColor(call.insights.averageSentiment)}`}
                  >
                    {call.insights.averageSentiment.charAt(0).toUpperCase() +
                      call.insights.averageSentiment.slice(1)}{" "}
                    Sentiment
                  </span>
                )}
                <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                  <MoreVertical size={16} className="text-gray-400" />
                </button>
              </div>
            </div>

            <div
              ref={transcriptContainerRef}
              className="p-6 space-y-6 flex-1 overflow-y-auto scroll-smooth"
            >
              {call.transcripts?.length > 0 ? (
                call.transcripts.map((tr, i) => {
                  const active = isTranscriptActive(tr.startTime, i);
                  return (
                    <div
                      key={i}
                      ref={(el) => (transcriptRefs.current[i] = el)}
                      className={`flex gap-4 group transition-all duration-300 ${active ? "bg-purple-500/10 -mx-2 px-2 py-2 rounded-lg" : ""}`}
                    >
                      <div className="flex flex-col items-center min-w-[52px]">
                        <button
                          onClick={() =>
                            handleTranscriptTimeClick(tr.startTime)
                          }
                          className={`text-xs cursor-pointer transition-colors ${active ? "text-purple-400 font-semibold" : "text-gray-400 hover:text-purple-400"}`}
                        >
                          {formatTranscriptTime(tr.startTime)}
                        </button>
                      </div>
                      <div className="flex-1">
                        <div
                          className={`text-sm font-medium mb-1 ${active ? "text-purple-300" : "text-white"}`}
                        >
                          {tr.speakerLabel || "Speaker"}:
                        </div>
                        <p
                          className={`text-sm leading-relaxed ${active ? "text-white" : "text-[#94979C]"}`}
                        >
                          {tr.text}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No transcript available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCallDetail;
